import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  colors,
  ConfirmPopup,
  EllipsisIcon,
  fontFamilies,
  Typography,
} from "@commonsku/styles";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import {
  SupplierColumn as BaseSupplierColumn,
  BillPurchaseOrdersColumn as BaseBillPurchaseOrdersColumn,
  BillReferenceNumberColumn as BaseBillReferenceNumberColumn,
  MultiProjectNumberColumn,
} from "../common";
import { useReportContext } from "../../report_context";
import { oauth } from "../../../../utils";
import { useHasCapabilities, useIdentity } from "../../../../hooks";
import { deleteBill, retrieveBill } from "../../../../redux/bills";
import DropdownMenu, { MenuTrigger } from "../../../DropdownMenu";
import { Bill } from "../../../../types/bills";
import BillPopup from "../../../bills/BillPopup";
import { getDefaultBillVendorCredit } from "../../../../redux/vendor_credits";
import { VendorCredit } from "../../../../types/vendor_credit";
import VendorCreditPopup from "../../../vendor-credit/VendorCreditPopup";

export const BillDateColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <>Bill&nbsp;Date</>,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["bill_date_billdate"],
  accessorKey: "bill_date_billdate",
  canSort: true,
  orderByField: "bill_date_billdate",
  width: 109,
};

export const DueDateColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <>Due&nbsp;Date</>,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["bill_date_due"],
  accessorKey: "bill_date_due",
  canSort: true,
  orderByField: "bill_date_due",
  width: 101,
};

export const AmountDueColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Money
> = {
  header: <>Amount&nbsp;Due</>,
  type: BASE_COLUMN_TYPES.Money,
  fields: ["bill_amount_due", "currency_id"],
  accessorKey: "bill_amount_due",
  canSort: true,
  orderByField: "bill_amount_due",
  width: 106,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ bill_amount_due, currency_id }) => ({
    amount: bill_amount_due,
    currency_id,
  }),
};

export const ExportDateColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <>Export&nbsp;Date</>,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["bill_date_exported"],
  accessorKey: "bill_date_exported",
  canSort: true,
  orderByField: "bill_date_exported",
  width: 151,
};

const StyledDropdownMenu = styled(DropdownMenu)`
  & > .dropdown-menu-trigger {
    color: ${colors.primary1.main};
    border-radius: 5px;
    padding: 4px 4px 0 4px;
  }
  &.dropdown-menu-open > .dropdown-menu-trigger {
    border: 2px solid ${colors.primary1.main};
  }
  &&& > ul {
    border: 2px solid ${colors.primary1.main};
    border-radius: 5px;
    margin-top: 4px;
    top: -0.5rem;
    right: 3rem;
    min-width: 200px;

    & > li {
      font-family: ${fontFamilies.regular};
      color: ${colors.neutrals.bodyText};
    }
  }
`;

function BillActions({ bill_id }: { bill_id: string }) {
  const identity = useIdentity();
  const canDeleteBill = useHasCapabilities(["DELETE-BILL"]);
  const canExport = useHasCapabilities(["EXPORT-REPORT"]);
  const canEditBill = useHasCapabilities(["UPDATE-BILL"]);
  const canCreateVendorCredit = useHasCapabilities(["CREATE-VENDOR-CREDIT"]);
  const { fetchReport } = useReportContext();
  const [showConfirm, setShowConfirm] = useState(false);
  const [bill, setBill] = useState<Bill | undefined>();
  const [vendorCredit, setVendorCredit] = useState<VendorCredit | undefined>();
  const dispatch = useDispatch();

  const bill_ids = useMemo(() => [bill_id], [bill_id]);

  const handleUnExport = useCallback(async () => {
    try {
      await oauth("POST", "bill", { action: "unexport", ids: bill_ids });
      fetchReport();
    } catch (e) {
      toast.error("Unable to un-export bill");
    }
  }, [bill_ids]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteBill(bill_id));
      fetchReport();
    } catch (e) {
      if (e?.json?.errors) {
        e.json.errors.forEach(toast.error);
      } else {
        toast.error("Unable to delete bill");
      }
    } finally {
      setShowConfirm(false);
    }
  }, [bill_id]);

  const handleBillClose = useCallback((saved: boolean) => {
    if (saved) {
      fetchReport();
    }
    setBill(undefined);
  }, []);

  const handleVendorCreditClose = useCallback(
    (saved: boolean) => {
      if (saved) {
        fetchReport();
      }
      setVendorCredit(undefined);
    },
    [fetchReport]
  );

  const options = useMemo(
    () =>
      [
        {
          value: "Un-Export",
          hideOnClick: true,
          onClick: handleUnExport,
          show: canExport,
        },
        {
          value: "Edit",
          hideOnClick: true,
          onClick: async () => {
            const bill = await retrieveBill(bill_id)(dispatch);
            setBill(bill);
          },
          show: canEditBill,
        },
        {
          value: "Create Vendor Credit",
          hideOnClick: true,
          onClick: async () => {
            const vendorCredit = await getDefaultBillVendorCredit(bill_id);
            setVendorCredit(vendorCredit);
          },
          show: canCreateVendorCredit,
        },
        {
          value: <span style={{ color: colors.error }}>Delete</span>,
          hideOnClick: true,
          onClick: () => setShowConfirm(true),
          show: canDeleteBill,
        },
      ].filter((o) => o.show),
    [bill_id]
  );
  return (
    <>
      {showConfirm && (
        <ConfirmPopup
          confirmText="Are you sure you want to delete?  Unpaid commissions will be recalculated."
          onClose={() => setShowConfirm(false)}
          onDelete={handleDelete}
        />
      )}
      {bill && <BillPopup bill={bill} onClose={handleBillClose} />}
      {vendorCredit && (
        <VendorCreditPopup
          vendorCredit={vendorCredit}
          onClose={handleVendorCreditClose}
        />
      )}
      <StyledDropdownMenu options={options} align="right">
        <MenuTrigger>
          <EllipsisIcon altText="Actions" />
        </MenuTrigger>
      </StyledDropdownMenu>
    </>
  );
}

const ActionColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Generic> = {
  header: "Actions",
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["bill_id"],
  accessorKey: "bill_id",
  canSort: false,
  width: 135,
  justify: "center",
  dataTransform: ({ bill_id }) => ({
    cell: <BillActions bill_id={bill_id} />,
  }),
};

const SupplierColumn = { ...BaseSupplierColumn, width: 227 };
const BillReferenceNumberColumn = {
  ...BaseBillReferenceNumberColumn,
  width: 121,
};
const BillPurchaseOrdersColumn = {
  ...BaseBillPurchaseOrdersColumn,
  width: 227,
};

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  BillReferenceNumberColumn,
  BillPurchaseOrdersColumn,
  SupplierColumn,
  BillDateColumn,
  DueDateColumn,
  AmountDueColumn,
  MultiProjectNumberColumn,
  ExportDateColumn,
  ActionColumn,
];
